// src/mediaservices/mediamtxService.ts
import { BroadcastStorage } from '@/utils/BroadcastStorage';
import { io, Socket } from 'socket.io-client';

class SimpleEventEmitter {
  private events: Record<string, Function[]> = {};
  on(event: string, cb: Function) { (this.events[event] ||= []).push(cb); }
  off(event: string, cb: Function) { this.events[event] = (this.events[event] || []).filter(f => f !== cb); }
  emit(event: string, payload?: any) { (this.events[event] || []).forEach(f => f(payload)); }
}

type ActiveEntry = {
  id: string;
  whepUrl: string;
  meta?: any;
};

export type HandRaiseRequest = {
  requestId: string;
  streamId: string;
  userId?: string;
  raised: boolean;
  timestamp?: number | string;
};

export type GenericRequestAckDto = {
  requestId: string;
  streamId: string;
  type: 'mute' | 'unmute' | 'host' | 'co-host' | 'hand-raise' | 'hand-lower' | 'video-pause' | 'video-unpause';
  received: boolean;
  processed?: boolean;
  message?: string;
  payload?: any;
};

// Optional ack options for admin methods (keeps function NAMES the same)
type AckOptions = {
  requestId?: string;
  processed?: boolean;
  message?: string;
  payload?: any;
};


class MediaMTXService {
  private wsUrl: string;
  private socket: Socket | null = null;
  private isInitializing: boolean = false;
  private isInitialized: boolean = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isAuthenticated: boolean = false;

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;

  /** All online publishers keyed by streamId */
  private active: Map<string, ActiveEntry> = new Map();

  /** Current role assignment (as streamIds), if any */
  private currentHostId: string | null = null;
  private currentCoHostId: string | null = null;

  private ee = new SimpleEventEmitter();

  // Add this property to track role switch acknowledgments
  private roleSwitchAcks = new Set<string>();

  constructor() {
    this.wsUrl = import.meta.env.VITE_MEDIAMTX_WS_URL || 'http://localhost:3000/mediamtx/operator';
  }

  on(
    event:
      | 'streamUpdate'
      | 'streamRemoved'
      | 'mute'
      | 'admin_mute'
      | 'video'
      | 'videoPauseState'
      | 'hand'
      | 'connected'
      | 'disconnected'
      | 'rolesChanged'
      | 'handRequest'
      | 'streamStatusUpdate'
      | 'handsState'
      | 'host_status_update'
      | 'handsState'
      | 'auth_error'
      | 'exception'
      | 'device_reconnecting'
      | 'reconnect_failed',
    cb: (p: any) => void
  ) { this.ee.on(event, cb); }

  off(
    event:
      | 'streamUpdate'
      | 'streamRemoved'
      | 'mute'
      | 'admin_mute'
      | 'video'
      | 'videoPauseState'
      | 'hand'
      | 'connected'
      | 'disconnected'
      | 'rolesChanged'
      | 'handRequest'
      | 'streamStatusUpdate'
      | 'handsState'
      | 'host_status_update'
      | 'handsState'
      | 'auth_error'
      | 'exception'
      | 'device_reconnecting'
      | 'reconnect_failed',
    cb: (p: any) => void
  ) { this.ee.off(event, cb); }

  /** Snapshot for screens that mount later */
  getStreamSummaries(): Array<{ id: string; whepUrl: string; meta?: any }> {
    return Array.from(this.active.values());
  }

  /** Snapshot of current role ids (may be nulls) */
  getCurrentRoles() {
    return { hostId: this.currentHostId, coHostId: this.currentCoHostId };
  }

  /** Get current host and co-host device names */
  getCurrentRoleNames() {
    let hostName: string | null = null;
    let coHostName: string | null = null;

    // Look up host name
    if (this.currentHostId) {
      const hostEntry = this.active.get(this.currentHostId);
      if (hostEntry?.meta) {
        // Try device_name first, then deviceSerialNumber as fallback
        hostName = hostEntry.meta.device_name || hostEntry.meta.deviceSerialNumber;
      }
    }

    // Look up co-host name
    if (this.currentCoHostId) {
      const coHostEntry = this.active.get(this.currentCoHostId);
      if (coHostEntry?.meta) {
        // Try device_name first, then deviceSerialNumber as fallback
        coHostName = coHostEntry.meta.device_name || coHostEntry.meta.deviceSerialNumber;
      }
    }

    return { hostName, coHostName };
  }

  async initialize(): Promise<void> {
    if (this.isInitializing) {
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return;
    }

    if (this.isInitialized && this.socket?.connected) {
      return;
    }

    this.isInitializing = true;

    try {
      await this.connect();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize MediaMTX service:', error);
      this.isInitialized = false;
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  private async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Clean up existing socket if any
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }


      this.socket = io(this.wsUrl, {
        transports: ['websocket'],
        upgrade: false,
        forceNew: true,
        autoConnect: true,
        reconnection: false, // manual reconnection
        timeout: 10000,
        pingTimeout: 60000,
        pingInterval: 25000,
        rememberUpgrade: false,
        upgradeTimeout: 30000
      });

      const connectionTimeout = setTimeout(() => {
        if (!this.socket?.connected) {
          console.error('MediaMTX connection timeout');
          reject(new Error('Connection timeout'));
        }
      }, 10000);

      this.socket.on('connect', () => {
        clearTimeout(connectionTimeout);
        this.reconnectAttempts = 0;
        this.ee.emit('connected', {});

        // small delay before auth
        setTimeout(() => {
          if (!this.socket?.connected) return;

          if (!this.isAuthenticated) {
            const authPayload = {
              role: 'admin',
              timestamp: new Date().toISOString(),
              clientId: `admin_${Date.now()}`,
              version: '1.0.0'
            };
            this.socket!.emit('auth_admin', authPayload);
          } else {
            console.log('MediaMTX: Already authenticated, skipping auth');
            resolve();
          }
        }, 100);
      });

      this.socket.on('exception', (data: any) => {        
        const errorData = data?.data || data;
        const eventType = data?.event;
        const errorMessage = errorData?.message || '';
        
        if (eventType === 'auth_admin' || 
            errorMessage.includes('Admin authentication required') ||
            errorMessage.includes('Operator is already logged in')) {
          
          // Emit auth error for the WebSocketProvider to handle
          this.ee.emit('auth_error', {
            type: 'authentication_failed',
            message: errorMessage,
            event: eventType,
            originalData: data
          });
          
          // If this happens during initial connection, reject the promise
          if (eventType === 'auth_admin') {
            if (errorMessage.includes("Operator is already logged in")) {
              reject(new Error("Operator is already logged in"));
            } else {
              reject(new Error(errorMessage || 'Authentication failed'));
            }
            return;
          }
        }
        
        this.ee.emit('exception', data);
      });

      this.socket.on('auth_admin', (response: any) => {        
        if (response.status === 'success') {
          this.isAuthenticated = true;
          resolve();
        } else {
          const errorMsg = response?.message || 'Authentication failed';
          
          if (errorMsg.includes("Operator is already logged in")) {
            this.ee.emit('auth_error', response);
            reject(new Error("Operator is already logged in"));
          } else {
            this.ee.emit('auth_error', response);
            reject(new Error(errorMsg));
          }
        }
      });

      // Server -> client event handlers
      this.socket.on('stream_status_update', (d: any) => this.handleStreamStatusUpdate(d));

      // Request-style events — ACK immediately
      this.socket.on('hand_raise_request', (d: any) => this.handleHandRaiseRequest(d));
      // Future-proofing: if backend emits these, we’ll ACK them too.
      this.socket.on('mute_request',        (d: any) => this.handleGenericRequest('mute', d));
      this.socket.on('unmute_request',      (d: any) => this.handleGenericRequest('unmute', d));
      this.socket.on('host_request',        (d: any) => this.handleGenericRequest('host', d));
      this.socket.on('co_host_request',     (d: any) => this.handleGenericRequest('co-host', d));
      this.socket.on('hand_lower_request',  (d: any) => this.handleHandRaiseRequest(d));

      this.socket.on('device_reconnecting', (data: any) => {
        this.ee.emit('device_reconnecting', data);
      });

      this.socket.on('success', (data: any) => {
        if (data.message === 'Admin authenticated successfully') {
          this.isAuthenticated = true;
          console.log('MediaMTX: Authentication confirmed, setting authenticated flag');
        }
      });

      this.socket.on('error', (error: any) => {
        console.error('MediaMTX WebSocket error:', error);
      });

      // Handle ping/pong for keepalive
      this.socket.on('ping', () => {
        console.log('MediaMTX: Received ping, sending pong');
        this.socket!.emit('pong');
      });

      this.socket.on('pong', () => {
        console.log('MediaMTX: Received pong');
      });

      // Additional debugging events
      this.socket.on('connect_timeout', () => {
        console.error('MediaMTX: Connection timeout');
      });

      this.socket.on('reconnect', () => {
        console.log('MediaMTX: Reconnected');
      });

      this.socket.on('reconnect_error', (error: any) => {
        console.error('MediaMTX: Reconnection error:', error);
      });

      this.socket.on('reconnect_failed', () => {
        console.error('MediaMTX: Reconnection failed');
      });

      this.socket.on('disconnect', (reason: string, details?: any) => {

        this.isInitialized = false;
        this.isAuthenticated = false;
        this.stopHeartbeat();
        this.ee.emit('disconnected', { reason, details });

        switch (reason) {
          case 'ping timeout':
            console.error('MediaMTX: Disconnected due to ping timeout - server not responding');
            break;
          case 'transport close':
            console.error('MediaMTX: Transport closed - network issue or server restart');
            break;
          case 'transport error':
            console.error('MediaMTX: Transport error - connection failed');
            break;
          case 'io server disconnect':
            console.log('MediaMTX: Server initiated disconnect - not auto-reconnecting');
            return;
          case 'io client disconnect':
            console.log('MediaMTX: Client initiated disconnect');
            return;
          default:
            console.log('MediaMTX: Unknown disconnect reason:', reason);
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          console.log('Unexpected disconnection, attempting to reconnect...');
          this.reconnect();
        } else {
          console.error('Max reconnection attempts reached, giving up');
        }
      });

      // Video pause ACK handler
    this.socket.on('generic_request_ack', (data: any) => {
      
      // Track role switch acknowledgments
      if ((data.type === 'host' || data.type === 'co-host') && data.acknowledged) {
        this.roleSwitchAcks.add(data.streamId);
        
        // Check if we have both acknowledgments (assuming 2 devices involved)
        if (this.roleSwitchAcks.size >= 2) {
          this.roleSwitchAcks.clear(); // Clear for next time
          
          // Trigger broadcast update
          setTimeout(() => this.updateBroadcastAfterRoleSwitch(), 1000);
        }
      }
  
      // Handle video pause/resume acknowledgments
      if (data.type === 'video-pause' || data.type === 'video-unpause') {
        const videoPaused = data.type === 'video-pause';
        
        this.ee.emit('video', {
          id: data.streamId,
          videoPaused: videoPaused,
          success: data.acknowledged !== false
        });
      }
    });

    // ✅ ADD: Handle admin_mute events in the Socket.IO listener
    this.socket.on('admin_mute', (data: { streamId: string; muted: boolean; timestamp: string }) => {
      // Update the mute state for this device
      const known = this.active.get(data.streamId);
      if (known) {
        // Update the stored mute state
        const currentMuteState = known.meta?.muteState || { selfMuted: false, adminMuted: false };
        const newMuteState = {
          ...currentMuteState,
          adminMuted: data.muted
        };
        
        known.meta = { ...(known.meta || {}), muteState: newMuteState };
        this.active.set(data.streamId, known);
        
        // Emit the mute event for components to listen
        this.ee.emit('mute', { id: data.streamId, muteState: newMuteState });
      }
    });
    });
  }

  // ----------------- Handlers -----------------

  /**
   * Robust handling:
   * - If publishersInfo is present & non-empty: refresh entries and compute removals.
   * - If publishersInfo is absent or empty: treat as incremental (e.g., mute-only). DO NOT remove entries.
   * - Always emit 'mute' for provided muteStates and update cache.
   */
  private handleStreamStatusUpdate(data: any): void {
    
    // Extract nested data from the new format
    const streamStatus = data?.streamStatus || {};
    const handStatus = data?.handStatus || {};
    const hostStatus = data?.hostStatus || {};
    
    this.handleStreamData(streamStatus);
    
    if (Object.keys(handStatus).length > 0) {
      this.handleHandData(handStatus);
    }
    
    // 3. Handle host data (same as old host_status_update)
    if (Object.keys(hostStatus).length > 0) {
      this.ee.emit('host_status_update', hostStatus);
    }
  }

  // Extract stream handling logic into separate method
  private handleStreamData(streamStatus: any): void {
    const publishers = streamStatus?.publishersInfo || {};
    const muteStates = streamStatus?.muteStates || {};
    const videoPauseStates = streamStatus?.videoPauseStates || {};
    
    const hasPublishers = Object.keys(publishers).length > 0;

    if (hasPublishers) {
      const currentIds = new Set<string>();

      Object.keys(publishers).forEach((id) => {
        currentIds.add(id);
        const info = publishers[id];
        console.log('Processing publisher info:', id, info);
        const meta = {
          userId: info.userId,
          deviceSerialNumber: info.deviceSerialNumber,
          device_name: info.deviceName || info.deviceSerialNumber,
          deviceGroup: info.deviceGroup,
          connectedAt: info.connectedAt,
          isHost: info.isHost,
          isCoHost: info.isCoHost,
          muteState: muteStates[id] ?? this.active.get(id)?.meta?.muteState,
          videoPauseState: videoPauseStates[id] ?? this.active.get(id)?.meta?.videoPauseState,
        };
        const entry: ActiveEntry = { id, whepUrl: info.whepUrl, meta };
        this.active.set(id, entry);
        this.ee.emit('streamUpdate', { id, whepUrl: entry.whepUrl, meta, online: true });
      });

      // Handle removals
      for (const oldId of Array.from(this.active.keys())) {
        if (!currentIds.has(oldId)) {
          const removed = this.active.get(oldId)!;
          this.active.delete(oldId);
          this.ee.emit('streamRemoved', { id: oldId, whepUrl: removed.whepUrl, online: false });
          if (this.currentHostId === oldId) this.currentHostId = null;
          if (this.currentCoHostId === oldId) this.currentCoHostId = null;
        }
      }
      
      let serverHostId: string | null = null;
      let serverCoHostId: string | null = null;
      
      Object.keys(publishers).forEach((id) => {
        const info = publishers[id];
        if (info.isHost === true) serverHostId = id;
        if (info.isCoHost === true) serverCoHostId = id;
      });

      const rolesChanged = (
        serverHostId !== this.currentHostId || 
        serverCoHostId !== this.currentCoHostId
      );
      
      if (rolesChanged) {
        this.currentHostId = serverHostId;
        this.currentCoHostId = serverCoHostId;
        this.ee.emit('rolesChanged', this.getCurrentRoles());
      }
    }

    // Emit original streamStatusUpdate for backward compatibility
    this.ee.emit('streamStatusUpdate', {
      publishersInfo: publishers,
      muteStates,
      videoPauseStates,
      broadcastViewerCount: streamStatus.broadcastViewerCount,
      liveStreamerCount: streamStatus.liveStreamerCount
    });

    // Handle mute states
    if (Object.keys(muteStates).length > 0) {
      Object.entries<any>(muteStates).forEach(([id, muteState]) => {
        const known = this.active.get(id);
        if (known) {
          known.meta = { ...(known.meta || {}), muteState };
          this.active.set(id, known);
        }
        this.ee.emit('mute', { id, muteState });
      });
    }

    if (Object.keys(videoPauseStates).length > 0) {
      Object.entries<any>(videoPauseStates).forEach(([id, videoPauseState]) => {
        const known = this.active.get(id);
        if (known) {
          known.meta = { ...(known.meta || {}), videoPauseState };
          this.active.set(id, known);
        }
        this.ee.emit('videoPauseState', { id, videoPauseState });
      });
    }
  }

    private currentHandsState: any[] = [];

    private handleHandData(handStatus: any): void {
      const handRaiseStates = handStatus?.handRaiseStates || {};
      
      const currentHands: any[] = [];
      
      Object.entries<any>(handRaiseStates).forEach(([id, handData]) => {
        const isHandRaised = typeof handData === 'boolean' 
          ? handData 
          : handData?.handRaiseStates || false;
        
        const networkQuality = typeof handData === 'object' 
          ? handData?.networkQuality 
          : undefined;
        
        const deviceId = typeof handData === 'object' 
          ? handData?.DeviceId 
          : undefined;
        
        const handRaiseTimestamp = typeof handData === 'object' 
          ? handData?.handRaiseTimestamp 
          : undefined;
        
        const deviceName = typeof handData === 'object' 
          ? handData?.deviceName 
          : undefined;
        
        const deviceType = typeof handData === 'object' 
          ? handData?.deviceType 
          : undefined;
        
        if (isHandRaised) {
          currentHands.push({
            id,
            isHandRaised: true,
            networkQuality,
            deviceId,
            handRaiseTimestamp,
            deviceName,
            deviceType
          });
        }
      });
      
      // ✅ Store current state
      this.currentHandsState = currentHands;
      
      this.ee.emit('handsState', currentHands);
    }

    // ✅ Add method to get current hands state
    getCurrentHandsState(): any[] {
      return [...this.currentHandsState];
    }

  // ---------- Request handlers (ACK "received") ----------

  /** Specialized handler for hand-raise (and lower via raised=false) requests */
  private handleHandRaiseRequest(data: any): void {
    const isRaise = data?.raised !== false; // default to raise when missing
    const req: HandRaiseRequest = {
      requestId: String(data?.requestId ?? ''),
      streamId: String(data?.streamId ?? ''),
      userId: data?.userId ? String(data.userId) : undefined,
      raised: isRaise,
      timestamp: data?.timestamp ?? Date.now(),
    };

    if (!req.requestId || !req.streamId) {
      console.warn('hand_raise_request missing requestId/streamId', data);
      return;
    }

    const ackType: GenericRequestAckDto['type'] = isRaise ? 'hand-raise' : 'hand-lower';

    // ACK RECEIVED with correct type
    this.sendRequestAck({
      requestId: req.requestId,
      streamId: req.streamId,
      type: ackType,
      received: true,
      processed: false,
      message: isRaise ? 'Hand-raise request received' : 'Hand-lower request received',
      payload: { userId: req.userId, timestamp: req.timestamp }
    });

    // Bubble to UI (you can also include an action field if helpful)
    this.ee.emit('handRequest', req);
  }



  /** Generic request receiver (for future request events) */
  private handleGenericRequest(type: GenericRequestAckDto['type'], data: any): void {
    const requestId = String(data?.requestId ?? '');
    const streamId  = String(data?.streamId ?? '');

    if (!requestId || !streamId) {
      console.warn(`${type}_request missing requestId/streamId`, data);
      return;
    }

    this.sendRequestAck({
      requestId,
      streamId,
      type,
      received: true,
      processed: true,
      message: `${type} request received`,
      payload: data
    });

    // For now we don't surface a UI event for generic requests.
    // Add if you later need panels for mute/host requests, etc.
  }

  // ----------------- Admin API (WS emits) -----------------

  private sendMessage(eventName: string, data: any): void {
    if (!this.socket?.connected) {
      console.error('MediaMTX WS not connected, cannot send:', eventName);
      return;
    }
    this.socket.emit(eventName, data);
  }

  /** Low-level ACK sender */


  private getAckEventName(type: GenericRequestAckDto['type']): string {
    switch (type) {
      case 'hand-raise': return 'operator_generic_request_ack';        // for "hand_raise_request"
      case 'hand-lower': return 'operator_generic_request_ack';  // for "hand_lower_request"
      case 'mute':       return 'operator_generic_request_ack';        // for "mute_request"
      case 'unmute':     return 'operator_generic_request_ack';      // for "unmute_request"
      case 'host':       return 'operator_generic_request_ack';        // for "host_request"
      case 'co-host':    return 'operator_generic_request_ack';     // for "co_host_request"
      default:           return 'operator_generic_request_ack';             // safe fallback (if server supports)
    }
  }

  private sendRequestAck(dto: GenericRequestAckDto) {
    if (!this.socket?.connected) {
      console.error('MediaMTX WS not connected, cannot send request ACK:', dto);
      return;
    }
    const eventName = this.getAckEventName(dto.type);
    this.socket.emit(eventName, dto);
  }
  /** Convenience: mark processed result for a previous request */
  private ackProcessed(
    requestId: string,
    streamId: string,
    type: GenericRequestAckDto['type'],
    processed: boolean,
    message?: string,
    payload?: any
  ) {
    this.sendRequestAck({ requestId, streamId, type, received: true, processed, message, payload });
  }

  /** Promote stream to Host */
  setHost(streamId: string, opts?: AckOptions) {
    this.sendMessage('set_host', { streamId, timestamp: new Date().toISOString() });
    this.currentHostId = streamId;
    if (this.currentCoHostId === streamId) this.currentCoHostId = null;
    this.ee.emit('rolesChanged', this.getCurrentRoles());

    // If this action is in response to a request, ACK processed
    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'host',
        opts.processed ?? true,
        opts.message ?? 'Host set successfully',
        opts.payload
      );
    }
  }

  /** Set stream as Co-Host (single co-host) */
  setCoHost(streamId: string, opts?: AckOptions) {
    this.sendMessage('set_co_host', { streamId, timestamp: new Date().toISOString() });
    this.currentCoHostId = streamId;
    if (this.currentHostId === streamId) this.currentHostId = null;
    this.ee.emit('rolesChanged', this.getCurrentRoles());

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'co-host',
        opts.processed ?? true,
        opts.message ?? 'Co-host set successfully',
        opts.payload
      );
    }
  }

   /** Remove Host role. If streamId omitted, clears the current host. */
    removeHost(streamId?: string) {
      const idToRemove = streamId ?? this.currentHostId ?? undefined;

      if (idToRemove) {
        // remove that specific host id
        this.sendMessage('remove_host', {
          streamId: idToRemove,
          timestamp: new Date().toISOString(),
        });
      }
    }

    /**
     * Remove Co-Host role.
     * - If streamId provided, removes that specific co-host.
     * - If omitted, clears the current co-host (if any).
     */
    removeCoHost(streamId?: string) {
      const idToRemove = streamId ?? this.currentCoHostId ?? undefined;

      if (idToRemove) {
        // your existing specific removal event
        this.sendMessage('remove_co_host', {
          streamId: idToRemove,
          timestamp: new Date().toISOString(),
        });
      }
    }



  /** Swap roles locally; server will follow with host_status_update */
  swapRoles(hostId?: string | null, coHostId?: string | null) {
    const current = this.getCurrentRoles();
    const newHost = coHostId ?? current.coHostId;
    const newCo   = hostId   ?? current.hostId;
    if (!newHost || !newCo) return;
    this.setHost(newHost);
    this.setCoHost(newCo);
  }

  // --------- BOOLEAN admin setters that match your DTOs ---------

  /** MuteActionDto: { streamId: string; muted: boolean } */
  setMicMuted(streamId: string, muted: boolean, opts?: AckOptions) {
    this.sendMessage('admin_mute', {
      streamId,
      muted,
      timestamp: new Date().toISOString(),
    });

    // ACK if this is settling a server-issued request
    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        muted ? 'mute' : 'unmute',
        opts.processed ?? true,
        opts.message ?? (muted ? 'Muted successfully' : 'Unmuted successfully'),
        opts.payload
      );
    }
  }

  setVideoPaused(streamId: string, videoPaused: boolean, opts?: AckOptions) {
  this.sendMessage('video_pause', {
    streamId,
    videoPaused,
    userId: undefined, // You can add userId if needed
    timestamp: new Date().toISOString(),
  });

  // ACK if this is settling a server-issued request
  if (opts?.requestId) {
    this.ackProcessed(
      opts.requestId,
      streamId,
      videoPaused ? 'video-pause' : 'video-unpause',
      opts.processed ?? true,
      opts.message ?? (videoPaused ? 'Video paused successfully' : 'Video resumed successfully'),
      opts.payload
    );
  }
}

  /** HandRaiseDto: { streamId: string; raised: boolean } */
  lowerHand(streamId: string, opts?: AckOptions) {
    this.sendMessage('hand_raise', {
      streamId,
      raised: false,
      timestamp: new Date().toISOString(),
    });

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'hand-lower',
        opts.processed ?? true,
        opts.message ?? 'Hand lowered',
        opts.payload
      );
    }
  }

  /** Accept a raised hand request (calls backend handler which promotes to co-host) */
  acceptHand(streamId: string, opts?: AckOptions) {
    this.sendMessage('admin_accept_hand', {
      streamId,
      timestamp: new Date().toISOString(),
    });

    // Emit local hand event to update UI immediately
    this.ee.emit('hand', { id: streamId, isHandRaised: false });

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'hand-raise', // This was a hand-raise that we're accepting
        opts.processed ?? true,
        opts.message ?? 'Hand accepted and promoted to co-host',
        opts.payload
      );
    }
  }

  /** Reject a raised hand request */
  rejectHand(streamId: string, opts?: AckOptions) {
    this.sendMessage('admin_reject_hand', {
      streamId,
      timestamp: new Date().toISOString(),
    });

    // Emit local hand event to update UI immediately
    this.ee.emit('hand', { id: streamId, isHandRaised: false });

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'hand-lower', // or create a new 'hand-reject' type if needed
        opts.processed ?? true,
        opts.message ?? 'Hand rejected by operator',
        opts.payload
      );
    }
  }

  /** Switch Host and Co-Host roles via backend logic */
  switchHostCoHost(streamId?: string, opts?: AckOptions) {
    // Clear previous acknowledgments
    this.roleSwitchAcks.clear();
    
    // ✅ Always send streamId - use provided value, active broadcast, or empty string
    const activeBroadcastId = BroadcastStorage.getStreamId();
    const targetStreamId = streamId || activeBroadcastId?.toString() || '';
    
    const payload = {
      streamId: targetStreamId, // ✅ Always include, empty string if no broadcast
      timestamp: new Date().toISOString(),
    };
    
    this.sendMessage('switch_host_co_host', payload);

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        targetStreamId || 'role-switch',
        'host',
        opts.processed ?? true,
        opts.message ?? 'Role switch initiated',
        opts.payload
      );
    }
  }

  // ----------------- Health / teardown -----------------

  /** Manual reconnection method for UI to call */
  async forceReconnect(): Promise<void> {
    console.log('MediaMTX: Force reconnection requested');
    this.isInitialized = false;
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    try {
      await this.connect();
      this.isInitialized = true;
      console.log('MediaMTX: Force reconnection successful');
    } catch (error) {
      console.error('MediaMTX: Force reconnection failed:', error);
      throw error;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        console.log('MediaMTX: Sending heartbeat');
        this.socket.emit('heartbeat', { timestamp: new Date().toISOString() });
      }
    }, 20000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private async reconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.ee.emit('reconnect_failed', { attempts: this.reconnectAttempts });
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(async () => {
      try {
        await this.connect();
        this.isInitialized = true;
      } catch (error) {
        console.error('Reconnection failed:', error);
        this.reconnect();
      }
    }, this.reconnectDelay);
  }

  isConnected(): boolean { return !!this.socket?.connected; }

  disconnect(): void {
    console.log('Disconnecting MediaMTX WebSocket service');
    this.isInitialized = false;
    this.isInitializing = false;
    this.isAuthenticated = false;
    this.stopHeartbeat();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.reconnectAttempts = 0;
    this.active.clear();
    this.currentHostId = null;
    this.currentCoHostId = null;
  }

  /**
   * Emit a VIDEO_START message to backend.
   * @param devicesToStart Array of device serials to start streaming
   * @param devicesToStop Array of device serials to stop streaming
   * @param reason Optional reason string
   */
  videoStart(devicesToStart: string[], devicesToStop: string[] = [], reason: string = 'visible-devices') {
    if (!this.socket?.connected) {
      console.error('MediaMTX WS not connected, cannot send video_start');
      return;
    }
    const payload = {
      devicesToStart,
      devicesToStop,
      reason,
      timestamp: new Date().toISOString(),
    };
    this.sendMessage('video_start', payload);
  }

  // Add this new method:
  private async updateBroadcastAfterRoleSwitch(): Promise<void> {
    try {
      const { BroadcastStorage } = await import('@/utils/BroadcastStorage');
      const { MediaMTXUpdateBroadcast } = await import('@/api/broadcastService');
      
      const streamId = BroadcastStorage.getStreamId();
      if (!streamId) {
        console.log('No active broadcast to update');
        return;
      }
      
      const devices = BroadcastStorage.getSelectedDevices();
      const streamName = BroadcastStorage.getBroadcastName();
      
      if (!devices?.length || !streamName) {
        console.log('Missing broadcast data');
        return;
      }
      
      const currentRoles = this.getCurrentRoles();
      const { hostId, coHostId } = currentRoles;
      
      // Ensure both role devices are in the broadcast
      const allDevices = [...new Set([...devices, hostId, coHostId].filter(Boolean))];
      
      const payload = {
        streamName: streamName.trim(),
        devices: allDevices.map(s => ({
          deviceSerial: s,
          isHost: s === hostId,
          isCoHost: s === coHostId,
          action: 'update' as const,
        })),
        newHostDeviceSerial: hostId,
      };
      
      
      const response = await MediaMTXUpdateBroadcast(Number(streamId), payload);
      
      // Update storage
      BroadcastStorage.storeSelectedDevices(allDevices);
      
      // Show success message
      import('@/components/AntComponents').then(({ message }) => {
        message.success(`Broadcast updated`);
      });
      
    } catch (error: any) {
      console.error('Failed to auto-update broadcast:', error);
    }
  }
}

/** Remove previous host/co-host before assigning new ones */
export async function assignHostAndCoHost(newHostId: string | null, newCoHostId: string | null) {
  const { hostId, coHostId } = mediamtxService.getCurrentRoles();

  // Remove old host if needed
  if (hostId && hostId !== newHostId) {
    mediamtxService.removeHost(hostId);
    await new Promise(res => setTimeout(res, 200));
  }
  // Remove old co-host if needed
  if (coHostId && coHostId !== newCoHostId) {
    mediamtxService.removeCoHost(coHostId);
    await new Promise(res => setTimeout(res, 200));
  }

  // Assign new host
  if (newHostId) mediamtxService.setHost(newHostId);
  // Assign new co-host
  if (newCoHostId) mediamtxService.setCoHost(newCoHostId);
}

// Export singleton instance
export const mediamtxService = new MediaMTXService();
export default MediaMTXService;
